import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'
import { DAIMI_AU_SLUG } from '@/content/daimiAutomotive'

/**
 * The Future 2030 Automotive outlook page — its copy and its figures, apart from
 * the component that draws them.
 *
 * **The first of the ten Future 2030 outlooks with a designed page of its own.**
 * `outlook()` in `content/reports.ts` writes all ten from one two-sentence
 * template — true of the series, not of any sector — under an amber note saying
 * the sector's own words had never been supplied. They were supplied on
 * 2026-08-28, *"referans alarak ilgili sayfayı tasarla"*, as a four-part full-page
 * capture of digitopia.co's Automotive outlook page plus the six images that page
 * carries: the digital-transformation cover art, the DMI forecast chart, the three
 * requirement photographs and the report mockup.
 *
 * **Why it is not the Industry Focus shape.** The nine designed report pages so
 * far are all cuts of the maturity study, and their bands are hero → findings +
 * figure → analysis → closing. This page is a different document: an outlook with
 * three named requirements, five predictions and a pointer at the maturity report
 * rather than the other way round. So the bands are the reference's, in the
 * reference's order, and the two shared closing bands
 * (`content/reportClosing.ts`) still sit where they sit on every other report
 * page.
 *
 * **Where the words come from.** Everything here is transcribed from the four
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/daimiAutomotive.ts` does, and `outlookAutomotiveChecks()` feeds them
 * into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form. These documents are prerendered with
 * no client bundle at all (`src/prerender.tsx`), so a form script would never run —
 * and the other thirty reports on this site hand the reader the PDF from our own
 * bucket instead. Every Download button here is that PDF, and
 * `OUTLOOK_AU_CLOSE.check` is what keeps the gating question open.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const OUTLOOK_AU_SLUG = 'automotive-industry-outlook-2030'

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets
 * it — one promise in bold, the reason for it underneath — and the button says
 * "Download the Report" rather than the "Download Report" every other band uses,
 * which is also the live page's wording.
 */
export const OUTLOOK_AU_INTRO = {
  lede: 'See what’s coming, and what it will take to stay ahead.',
  body: '2030 is already taking shape. This outlook reveals the critical moves that will define the winners of tomorrow.',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the wireframed car on a circuit
   * board, which is the artwork behind the report cover with none of its lettering
   * on it. The live hero has nothing beside the words but the gradient ribbon; this
   * is the file whose own name carries this page's slug, so it is the one image the
   * page is certainly meant to own.
   */
  photo: {
    src: '/photo/future-2030-automotive-digital-transformation.webp',
    alt: 'A car rendered as a wireframe of light on a circuit board',
  } satisfies Photo,
  check:
    'The hero’s two lines are transcribed from the reference capture rather than supplied as text. The live hero is also empty to the right of the words but for the gradient ribbon — the digital-transformation image is placed there here because its filename carries this page’s slug and it would otherwise appear nowhere. Say if that emptiness was the point.',
}

/**
 * The outlook band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one bulleted block on the live page in which each line opens with
 * a bolded label — "Laggards", "Average performers", "Digital leaders". Split into
 * `label` and `body` here so the label can be set rather than emphasised inside a
 * sentence, which is the call every designed report page on this site has made.
 */
export const OUTLOOK_AU_FIGURE = {
  heading: 'Automotive Industry Outlook',
  points: [
    {
      label: 'Laggards',
      body: 'who fail to modernize may plateau at a DMI of ~2.06, risking irrelevance.',
    },
    {
      label: 'Average performers',
      body: 'will hover just above survival, making marginal gains without differentiation.',
    },
    {
      label: 'Digital leaders',
      body: 'that continuously invest in AI, integration, and culture could surpass a DMI of 4.0, setting the standard for resilience and profitability.',
    },
  ],
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories
   * each fanning out into an optimistic, moderate and pessimistic band. It is
   * 1200×784 on a white ground, so it gets a white card of its own and scales to it
   * whole — ten year columns with three labelled values each do get small on a
   * phone, but a figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-automotive-dmi-forecast.webp',
    alt: 'Automotive DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Automotive Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Figure 1 is the file you supplied and is shown whole. Two readings wanted on the words beside it. The three trajectories are transcribed from the reference capture, including the two figures they turn on — a laggard plateau at “a DMI of ~2.06” and leaders “surpass a DMI of 4.0”. And the live page calls the index DMI here while every other page on this site calls it DAIMI, the Digital & AI Maturity Index; the chart itself is lettered DMI, so both were left as they are. Say which name this page should use.',
}

/** The dark requirements band: two headline lines, then three cards. */
export const OUTLOOK_AU_REQUIREMENTS = {
  heading: 'Past performance doesn’t guarantee future leadership.',
  subheading: 'The gap is widening, and the time to act is now.',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'End-to-End Integration',
      body: 'Break silos. Connect your entire ecosystem, from suppliers to service.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/automotive-showroom-cars.webp',
        alt: 'New cars lined up in a dealership showroom',
      } satisfies Photo,
    },
    {
      title: 'AI-Driven Insight',
      body: 'Turn data into decisions. Use analytics and AI to enhance forecasting, agility, and experience.',
      linkLabel: 'Enterprise AI Solutions',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/automotive-ai-data-analytics.webp',
        alt: 'Someone reading dashboards on a tablet, charts floating above the screen',
      } satisfies Photo,
    },
    {
      title: 'Empowered People & Culture',
      body: 'Upskill your workforce and foster a digital-first mindset. True transformation begins with people.',
      linkLabel: 'Transformation Management',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/automotive-people-and-culture.webp',
        alt: 'An executive reading a tablet, a bar chart rising out of it',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card texts are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends it to a Digital Upskilling solution page, which digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations.',
}

/** The predictions band, with the report mockup beside it. */
export const OUTLOOK_AU_PREDICTIONS = {
  heading: 'Five Bold Predictions Shaping the Future of Automotive',
  items: [
    {
      label: 'The Profitability Gap Will Widen',
      body: 'Digital leaders will be 15–20% more profitable.',
    },
    {
      label: 'Digital Leaders Will Drive Volume Growth',
      body: 'Efficiency will outpace legacy scale.',
    },
    {
      label: 'AI & Automation Will Restructure Labor',
      body: 'Up to 30% lower labor costs through tech.',
    },
    {
      label: 'AI and Robotics Will Become Non-Negotiable',
      body: 'From differentiators to industry baseline.',
    },
    {
      label: 'Digital Maturity Will Define Market Position',
      body: 'Winners will lead markets, not just survive.',
    },
  ],
  /** The mockup supplied with this request — the Future 2030 cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-automotive-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Automotive',
  } satisfies Photo,
  check:
    'All five predictions are transcribed from the reference capture, and three of them carry a number the report has to stand behind: 15–20% more profitable, up to 30% lower labor costs, and a laggard plateau of ~2.06 in the band above. Read them against the PDF before production.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's.** On
 * digitopia.co it sends the reader to a *2024* Automotive Digital Maturity Report.
 * This site has no such report: its automotive maturity cut is the Industry Focus
 * cut of the Digital & AI Maturity Report 2026, which has a designed page of its
 * own (`content/daimiAutomotive.ts`). So the CTA points there, the cover shown is
 * that report's, and "five years of in-depth assessments" is "six years" — the
 * figure this site's own closing band and that report's Figure 1 both state.
 * `check` says all three out loud rather than letting them look like a
 * transcription.
 */
export const OUTLOOK_AU_MATURITY = {
  heading: 'Before You Plan for 2030, Understand Where You Are Today',
  body: 'The road to digital leadership starts with clarity. Our Automotive Industry Focus cut of the Digital & AI Maturity Report 2026 benchmarks your industry’s current state, based on real data from six years of in-depth assessments.',
  points: [
    'Discover where your organization stands in relation to leaders, laggards, and the industry average.',
    'Use these insights to validate your priorities and define the right next steps.',
  ],
  ctaLabel: 'Explore the Automotive Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_AU_SLUG}`,
  /** That report's cover, which this site already holds at 2560px. */
  photo: {
    src: '/photo/daimi-report-2026-automotive-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Automotive Focus, February 2026',
  } satisfies Photo,
  check:
    'This band is adapted rather than transcribed, and a person should confirm the adaptation. The live page offers a “2024 Automotive Digital Maturity Report” with its own cover and CTA; this site publishes the Automotive Industry Focus cut of the 2026 report instead, so the cover, the CTA label and the link are that report’s. The live sentence also says “five years of in-depth assessments” where this site says six. Say if a 2024 edition is meant to be published here, and the band should then point at it.',
}

/** The closing band, which is where the live page's gated form sits. */
export const OUTLOOK_AU_CLOSE = {
  heading: 'Shape Your Future, Starting Today',
  body: '2030 isn’t far away. The transformation starts with insight and action. Download the full Future 2030: Automotive report or speak with a transformation expert.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiAutomotiveChecks()` uses, and appended to
 * the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file. This page
 * arrived with all six of its images. What is left is transcription nobody has
 * read against the original, one destination this site cannot offer, one band
 * adapted to the report this site actually publishes, and the gating decision.
 */
export function outlookAutomotiveChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_AU_INTRO.check },
    { section: 'outlook', check: OUTLOOK_AU_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_AU_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_AU_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_AU_MATURITY.check },
    { section: 'download', check: OUTLOOK_AU_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_AU_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
